import * as React from 'react';
import { useState, useEffect, useRef } from 'react';

// 全顯示倍率(fitScale) = 將 children 的尺寸調整成跟可視區域一樣時的倍率
// scaleFactor: 調整全顯示倍率的因數。實際倍率 = fitScale * scaleFactor

/** 一個可視區域，可以縮放、位移地顯示一塊平面空間 */
export default function ({ oriSize, viewSize, onInit, children }: {
    oriSize: size,              // 未縮放前的實際大小
    viewSize: size,             // 可見區域大小
    onInit: (controller: controller) => void,
    children: JSX.Element | JSX.Element[],
}) {
    const [scaleFactor, setScaleFactor] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    const state = useRef({ oriSize, viewSize, scaleFactor, offset });
    state.current = { oriSize, viewSize, scaleFactor, offset };

    useEffect(() => {
        onInit({ fit, zoomin, applyOffsetDelta, applyScaleDelta });
    }, []);

    const fitScale = {
        width: viewSize.width / oriSize.width,
        height: viewSize.height / oriSize.height,
    }

    return (
    // 負責裁切到可視區域
    <div style={{
        width: viewSize.width,
        height: viewSize.height,
        overflow: 'hidden',
    }}>
        <div style={{
            position: 'relative',       /* 給 position=absolute 的子元件定位使用 */
            width: oriSize.width,
            height: oriSize.height,
            transform: `scale(${fitScale.width * scaleFactor}, ${fitScale.height * scaleFactor})`,
            transformOrigin: 'top left',
            top: offset.y,
            left: offset.x,
            // transitionProperty: 'transform, left, top',
            // transitionDuration: '0.2s',
            // transitionTimingFunction: 'ease-out',
        }}>
            {children}
        </div>
    </div>);

    function fit() {
        setScaleFactor(1);
        setOffset({ x: 0, y: 0 });
    }

    function zoomin(zone: '左上' | '左下' | '右上' | '右下') {
        const { oriSize, viewSize, scaleFactor, offset } = state.current;
        setScaleFactor(2);
        if (zone === '左上') {
            setOffset({ x: 0, y: 0 });
        } else if (zone === '左下') {
            setOffset({ x: 0, y: viewSize.height * -1 });
        } else if (zone === '右上') {
            setOffset({ x: viewSize.width * -1, y: 0 });
        } else if (zone === '右下') {
            setOffset({ x: viewSize.width * -1, y: viewSize.height * -1 });
        } else {
            const _: never = zone;
        }
    }

    function applyOffsetDelta(delta: offset) {
        const { oriSize, viewSize, scaleFactor, offset } = state.current;
        setOffset(calcOffset(oriSize, viewSize, scaleFactor, offset, delta));
    }

    /**
     * @param pin 縮放中心點在可見區域上的座標 (但可以超出可見區域的範圍)
     */
    function applyScaleDelta(scaleFactorDelta: number, pin: position) {
        const { oriSize, viewSize, scaleFactor, offset } = state.current;
        const dstScale = Math.max(scaleFactor + scaleFactorDelta, 1);
        // 縮放後 pin 點的位置
        const pin2 = {
            x: (((offset.x * -1) + pin.x) * (dstScale / scaleFactor)) + offset.x,
            y: (((offset.y * -1) + pin.y) * (dstScale / scaleFactor)) + offset.y,
        }
        // 因為 pin 點位置需一致，計算出與目標的差異
        const delta = {
            x: pin.x - pin2.x,
            y: pin.y - pin2.y,
        }
        setScaleFactor(dstScale);
        setOffset(calcOffset(oriSize, viewSize, dstScale, offset, delta));
    }
}

export type controller = {
    fit: () => void,
    zoomin: (zone: '左上' | '左下' | '右上' | '右下') => void,
    applyOffsetDelta: (delta: offset) => void,
    applyScaleDelta: (scaleFactorDelta: number, pin: position) => void,
}

export const noop: controller = {
    fit: () => {},
    zoomin: (zone: '左上' | '左下' | '右上' | '右下') => {},
    applyOffsetDelta: (delta: offset) => {},
    applyScaleDelta: (scaleFactorDelta: number, pin: position) => {},
}

/** 給一個目標 delta，計算出不會造成空白邊的 delta 值 */
function calcOffset(oriSize: size, viewSize: size, scaleFactor: number, offset: offset, delta: offset) {
    const fitScale = {
        width: viewSize.width / oriSize.width,
        height: viewSize.height / oriSize.height,
    }
    const scale = {
        w: fitScale.width * scaleFactor,
        h: fitScale.height * scaleFactor,
    }
    const minX = viewSize.width - (oriSize.width * scale.w);
    const minY = viewSize.height - (oriSize.height * scale.h);
    const max = 0;
    return {
        x: restrict(offset.x + delta.x, minX, max),
        y: restrict(offset.y + delta.y, minY, max),
    }
}

/** (3, 0, 10) = 3, (-1, 0, 10) = 0, (13, 0, 10) = 10 */
function restrict(n: number, min: number, max: number) {
    if (n > max) {
        return max;
    } else if (n < min) {
        return min;
    } else {
        return n;
    }
}

type size = {
    width: number,
    height: number,
}

type offset = {
    x: number,
    y: number,
}

type position = offset;
type viewport = size & position;
